import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";

function HeroCarousel() {
  const { tr, settings } = useApp();
  const slides = settings.heroSlides || [];
  const [i, setI] = useState(0);
  useEffect(() => {
    if (slides.length <= 1) return;
    const id = setInterval(() => setI((p) => (p + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [slides.length]);
  if (!slides.length) return null;

  return (
    <section className="relative h-[62vh] md:h-[82vh] overflow-hidden bg-neutral-900">
      {slides.map((s, idx) => (
        <div key={s.id} className={`absolute inset-0 transition-opacity duration-1000 ${idx === i ? "opacity-100" : "opacity-0"}`}>
          <img src={s.image} alt={tr(s.title)} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
          <div className="absolute inset-0 flex flex-col items-center justify-end md:justify-center text-center px-6 pb-20 md:pb-0">
            <h1 className="brand-font text-white text-4xl md:text-7xl tracking-wide mb-4 fade-up">{tr(s.title)}</h1>
            <p className="text-white/85 text-sm md:text-lg font-light tracking-wide max-w-xl mb-8 fade-up">{tr(s.subtitle)}</p>
            <Link to={s.link || "/shop"} className="inline-block border border-white text-white text-xs tracking-[0.25em] uppercase px-9 py-3.5 hover:bg-white hover:text-neutral-900 transition-colors duration-300 fade-up">
              {tr(s.cta)}
            </Link>
          </div>
        </div>
      ))}
      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {slides.map((_, idx) => (
            <button key={idx} onClick={() => setI(idx)} className={`h-[3px] transition-all duration-300 ${idx === i ? "w-8 bg-white" : "w-4 bg-white/40"}`} />
          ))}
        </div>
      )}
    </section>
  );
}

function Section({ title, link, products }) {
  const { t } = useApp();
  if (!products.length) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-16 md:py-20">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="w-12 h-[2px] mb-4" style={{ backgroundColor: "var(--brand)" }} />
          <h2 className="brand-font text-3xl md:text-4xl tracking-wide text-neutral-900">{title}</h2>
        </div>
        {link && (
          <Link to={link} className="hidden md:flex items-center gap-1.5 text-xs tracking-widest uppercase text-neutral-600 hover:text-[color:var(--brand)] transition-colors">
            {t("viewAll")} <ChevronRight size={15} />
          </Link>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 md:gap-x-6">
        {products.map((p) => <ProductCard key={p.id} product={p} />)}
      </div>
      {link && (
        <div className="md:hidden mt-8 text-center">
          <Link to={link} className="inline-block border border-neutral-300 text-xs tracking-widest uppercase px-8 py-3 text-neutral-700">{t("viewAll")}</Link>
        </div>
      )}
    </section>
  );
}

function CategoryBanner() {
  const { tr, categories } = useApp();
  const banners = categories.slice(0, 3);
  const imgs = [
    "https://images.unsplash.com/photo-1523170335258-f5ed11844a49?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    "https://images.unsplash.com/photo-1751437774882-deeea4352018?auto=format&fit=crop&w=800&q=80",
    "https://images.unsplash.com/photo-1635462684825-3621c1df5403?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  ];
  return (
    <section className="max-w-[1400px] mx-auto px-4 md:px-8 py-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        {banners.map((c, i) => (
          <Link key={c.id} to={`/shop/${c.slug}`} className="relative aspect-[4/5] md:aspect-[3/4] overflow-hidden group">
            <img src={imgs[i]} alt={tr(c.name)} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-black/30 group-hover:bg-black/45 transition-colors" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <h3 className="brand-font text-white text-2xl md:text-3xl tracking-wide mb-3">{tr(c.name)}</h3>
              <span className="text-white text-[10px] tracking-[0.3em] uppercase border-b border-white/60 pb-1">Discover</span>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const { t, products } = useApp();
  const by = (s) => products.filter((p) => p.section === s).slice(0, 8);
  const featured = by("featured").length ? by("featured") : products.slice(0, 8);
  const allNewest = [...products].sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)).slice(0, 8);

  return (
    <div>
      <HeroCarousel />
      <Section title={t("featured")} link="/shop/erkek-saatler" products={featured} />
      <CategoryBanner />
      <Section title={t("womenSection")} link="/shop/bayan-saatler" products={by("women")} />
      <Section title={t("collectionSection")} link="/shop/koleksiyonlar" products={by("collection")} />
      <Section title={t("accessorySection")} link="/shop/aksesuar" products={by("accessory")} />
      <div className="bg-neutral-50">
        <Section title={t("shop")} link="/shop" products={allNewest} />
      </div>
    </div>
  );
}
